import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {StockService} from "../../services/stock/stock.service";
import {DesiredStockItem} from "../../models/stock";
import {NgClass} from "@angular/common";
import {faCartShopping, faMinus, faPen, faPlus, faSave, faTimes, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {FormsModule} from "@angular/forms";
import {Subject, Subscription} from "rxjs";
import {UiCardComponent, UiButtonComponent} from "../../shared/ui";
import { AppLogger, AppLoggerService } from '../../core/logging';

@Component({
  selector: 'app-desired-stock',
  standalone: true,
  imports: [
    FaIconComponent,
    FormsModule,
    NgClass,
    UiCardComponent,
    UiButtonComponent
  ],
  templateUrl: './desired-stock.component.html',
  styleUrl: './desired-stock.component.scss'
})
export class DesiredStockComponent implements OnInit {

  private readonly log: AppLogger;

  @Input() householdId: number = undefined;
  @Input() productId: number = undefined;
  @Input() showStatusLabel: boolean = true;
  /** When true, the restock nudge ("Add to Groceries") is shown while below target. */
  @Input() canAddToList: boolean = false;
  @Input() updateTrigger: Subject<boolean> = new Subject<boolean>();
  @Output() changed = new EventEmitter<void>();
  /**
   * Emitted when the user taps the restock nudge. Carries the number missing
   * to reach the target so the parent can add it as the item's quantity.
   */
  @Output() addToList = new EventEmitter<number>();

  desiredStockItem: DesiredStockItem;
  desiredStockItemSet: boolean = false;
  desiredUpdate: number = 0;

  isEditMode: boolean = false;
  updateSubscription: Subscription;
  isLoading: boolean = true;
  private loadingTimeout: any;
  private hasSubscribedToUpdates = false;

  // Icons
  faEdit = faPen;
  faSave = faSave;
  faRemove = faTrash;
  faClose = faTimes;
  faPlus = faPlus;
  faMinus = faMinus;
  faCart = faCartShopping;

  constructor(private stockService: StockService, private appLogger: AppLoggerService) {
    this.log = this.appLogger.createForClass(DesiredStockComponent);
  }

  listenToUpdates(): void {
    this.updateSubscription = this.updateTrigger.subscribe(update => {
      if (update) {
        this.log.debug('Desired stock update triggered', { householdId: this.householdId, productId: this.productId });
        this.ngOnInit();
      }
    });
  }

  ngOnInit(): void {
    this.isEditMode = false;
    this.desiredStockItem = undefined;
    if (!this.hasSubscribedToUpdates) {
      this.listenToUpdates();
      this.hasSubscribedToUpdates = true;
    }
    this.isLoading = false;
    this.clearLoading();
    this.loadingTimeout = setTimeout(() => {
      this.isLoading = true;
      this.log.debug('Showing loading indicator for desired stock', { householdId: this.householdId, productId: this.productId });
    }, 50);
    this.stockService.getDesiredStockByProductId(this.householdId, this.productId)
      .subscribe({
        next: (desiredStock) => {
          this.desiredStockItem = desiredStock;
          this.desiredStockItemSet = true;
          this.clearLoading();
        },
        error: (err) => {
          this.clearLoading();
          if (err.status === 404) {
            this.desiredStockItemSet = false;
          } else {
            this.log.error('Unexpected error while loading desired stock', err, { householdId: this.householdId, productId: this.productId });
          }
        }
      });
  }

  enableEditMode() {
    this.isEditMode = true;
    this.desiredUpdate = this.desiredStockItem ? this.desiredStockItem.desired : 0;
  }

  saveDesiredStock() {

    if (!this.desiredUpdate || !this.householdId || !this.productId) {
      this.log.warn('Invalid data for saving desired stock', { desiredUpdate: this.desiredUpdate, householdId: this.householdId, productId: this.productId });
      return;
    }

    this.stockService.updateDesiredStock(this.householdId, this.productId, this.desiredUpdate)
      .subscribe({
        next: (response) => {
          this.log.info('Desired stock updated successfully', { response, householdId: this.householdId, productId: this.productId });
          this.notifyChange();
          this.ngOnInit();
        },
        error: (err) => {
          this.log.error('Error updating desired stock', err, { householdId: this.householdId, productId: this.productId, desiredUpdate: this.desiredUpdate });
        }
      });
  }

  cancelEdit() {
    this.ngOnInit();
  }

  notifyChange() {
    this.changed.emit();
  }

  clearDesiredStock() {
    if (!this.householdId || !this.productId) {
      this.log.warn('Invalid data for clearing desired stock', { householdId: this.householdId, productId: this.productId });
      return;
    }

    this.stockService.clearDesiredStock(this.householdId, this.productId)
      .subscribe({
        next: (response) => {
          this.log.info('Desired stock cleared successfully', { response, householdId: this.householdId, productId: this.productId });
          this.notifyChange();
          this.ngOnInit();
        },
        error: (err) => {
          this.log.error('Error clearing desired stock', err, { householdId: this.householdId, productId: this.productId });
        }
      });
  }

  // ── Derived view state ───────────────────────────────────────────────
  /** Amount currently on hand (available across all stores). */
  get have(): number {
    return this.desiredStockItem?.available ?? 0;
  }

  /** The desired target quantity. */
  get target(): number {
    return this.desiredStockItem?.desired ?? 0;
  }

  /** Whether a (positive) target has been set. */
  get hasTarget(): boolean {
    return this.desiredStockItemSet && (this.desiredStockItem?.desired ?? 0) > 0;
  }

  /** How many are missing to reach the target. */
  get missing(): number {
    const m = this.desiredStockItem?.missing;
    return m != null ? Math.max(0, m) : Math.max(0, this.target - this.have);
  }

  /** Meter fill (0–100), capped at the target. */
  get fillPercent(): number {
    if (!this.target) return 0;
    return Math.max(0, Math.min(100, (this.have / this.target) * 100));
  }

  get statusKey(): 'ok' | 'low' | 'out' | 'none' {
    if (!this.hasTarget) return 'none';
    if (this.have >= this.target) return 'ok';
    if (this.have <= 0) return 'out';
    return 'low';
  }

  get statusText(): string {
    switch (this.statusKey) {
      case 'ok': return 'In stock';
      case 'low': return 'Low';
      case 'out': return 'Out of stock';
      default: return 'No target set';
    }
  }

  stepUp(): void {
    this.desiredUpdate = (Number(this.desiredUpdate) || 0) + 1;
  }

  stepDown(): void {
    this.desiredUpdate = Math.max(0, (Number(this.desiredUpdate) || 0) - 1);
  }

  requestAddToList(): void {
    this.addToList.emit(this.missing);
  }

  private clearLoading() {
    this.log.trace(
      "Clearing loading indicator, data fetch completed"
    )
    clearTimeout(this.loadingTimeout);
    this.isLoading = false;
  }
}
